import { ActivityVariable } from "./activity";

/** Intent of a performance (PerformanceLog.md §1.2). Presence on a training log marks it as a performance. */
export type PerformanceType = "competition" | "gameDay" | "testing";

export interface TrainingLog {
  dateID: string; // YYYYMMDD format from parent day post
  load: number;
  muscular: number;
  notes: string;
  respiratory: number;
  rpe: number;
  timestamp: number; // GMT timestamp
  tag: string[];
  trainingTypeID?: string; // ID reference into the trainingTypes collection
  duration: number; // duration in minutes
  trainingPerformed?: number; // training performed in minutes (optional when RPE < 7.5)
  linkedActivityLogID?: string;
  /**
   * Sessions linked to this log, ordered; element 0 is the primary session.
   * Source of truth for the log -> session link. Read via
   * `getLinkedSessionIDs` / `getPrimarySessionID` (utils/sessionLinks).
   */
  linkedSessionIDs?: string[];
  /**
   * Legacy scalar. New writes keep it equal to `linkedSessionIDs[0]` (and
   * delete it when the array is empty). Logs that only have this field are
   * still read by falling back to it, so they do not need a migration.
   */
  linkedSessionID?: string;
  pain?: boolean;
  injured?: boolean;
  public?: boolean; // Whether this log is publicly readable
  linkedPostID?: string; // ID of the post this log is linked to
  /** Presence marks this training log as a PERFORMANCE (PerformanceLog.md §1.2). */
  performanceType?: PerformanceType;
  eventId?: string; // linked event id → events/{uid}/events/{id} (optional)
  eventDay?: number; // 1-based day within a multi-day event (set with eventId)
}

export interface RecoveryLog {
  dateID: string; // YYYYMMDD format from parent day post
  notes: string;
  timestamp: number; // GMT timestamp
  selectedRecovery: string[];
  pointsAmount?: number;
}
export interface NegativeInfluencesLog {
  dateID: string;
  notes: string;
  timestamp: number;
  selectedInfluences: string[];
  pointsAmount?: number; // stored as positive magnitude
}
export interface NiggleLog {
  dateID: string; // YYYYMMDD format from parent day post
  impact: number;
  notes: string;
  pain: number;
  timestamp: number; // GMT timestamp
  linkedTrainingLogID?: string;
}

// Activity captured directly on a performance log (single source of truth).
// Holds all of the selected activity's variables, each with its measured value
// and unit, so the log is self-contained and queryable for charts.
export interface PerformanceActivity {
  activityTypeID: string;
  activityName: string;
  variables: ActivityVariable[];
  globalCategoryID?: string; // Set when the activity comes from a global activity pack
}

export interface PerformanceLog {
  dateID: string; // YYYYMMDD format from parent day post
  notes: string;
  performanceExpectation: number; // 1-10 performance rating
  performanceResult: string; // Manual free-text result (fallback when no activity is selected)
  performanceTitle?: string; // Optional title for the performance
  performanceDate?: string; // YYYYMMDD - the single date of performance (may differ from when it was logged)
  activity?: PerformanceActivity; // Activity logged directly on this performance (source of truth)
  timestamp: number; // GMT timestamp - audit "logged at" value
  tag?: string[]; // Tags like "PB" (Personal Best) or "SB" (Season's Best)
  public?: boolean; // Whether this log is publicly readable
  linkedPostID?: string; // ID of the post this log is linked to
  linkedSessionID?: string; // Legacy: ID of the session older logs were linked to
  linkedTrainingLogID?: string; // ID of the training log this performance log is linked to (always the performance's training log when logged via the Performance flow)
  eventId?: string; // copied from the parent training log
}

export interface HypnogramSample {
  timestamp: string;
  level: number;
}

export interface HealthLog {
  dateID: string; // YYYYMMDD format from parent day post
  hrv?: number;
  respRate?: number;
  rhr?: number;
  notes?: string;
  sleepStartTs?: string | any;
  sleepEndTs?: string | any;
  timestamp: number; // GMT timestamp
  terraUserID?: string;
  sleepDuration?: number;
  sleepTimeline?: HypnogramSample[]; // Sleep timeline data from Terra
  /** Metric keys the athlete typed. Wearable sync leaves these alone. */
  userFields?: string[];
  /** Source resource (e.g. "GARMIN") when known — used for brand attribution. */
  provider?: string;
}

export interface StepLog {
  dateID: string; // YYYYMMDD format from parent day post
  notes: string;
  timestamp: number; // GMT timestamp
  steps: number;
}

export interface NoteLog {
  dateID: string; // YYYYMMDD format from parent day post
  notes: string;
  timestamp: number; // GMT timestamp
}

export interface FemaleCycleLog {
  dateID: string; // YYYYMMDD format from parent day post
  id?: string;
  cycleStartDate: string; // ISO date string (YYYY-MM-DD)
  timestamp: number; // GMT timestamp when log was created
  dayPostID?: string; // ID of the day post this log is linked to
  notes?: string; // Optional notes about the cycle
}

// Activity heart rate data types (cached Terra shape)
export interface HeartRateZone {
  duration_seconds?: number;
  end_percentage?: number;
  zone?: number;
  name?: string;
  start_percentage?: number;
}

export interface HeartRateSample {
  context?: number;
  timestamp?: string;
  timer_duration_seconds?: number | null;
  bpm?: number;
}

export interface ActivityHeartRateData {
  summary?: {
    avg_hrv_rmssd?: number | null;
    avg_hr_bpm?: number;
    resting_hr_bpm?: number | null;
    max_hr_bpm?: number;
    avg_hrv_sdnn?: number | null;
    user_max_hr_bpm?: number | null;
    min_hr_bpm?: number;
    hr_zone_data?: HeartRateZone[];
  };
  detailed?: {
    hrv_samples_sdnn?: any[];
    hrv_samples_rmssd?: any[];
    hr_samples?: HeartRateSample[];
  };
}

// Position data interfaces
export interface PositionSample {
  timestamp: string;
  coords_lat_lng_deg: [number, number]; // [latitude, longitude] in degrees
  timer_duration_seconds: number;
}

export interface PositionData {
  position_samples?: PositionSample[];
  center_pos_lat_lng_deg?: [number, number]; // [latitude, longitude] in degrees
  start_pos_lat_lng_deg?: [number, number]; // [latitude, longitude] in degrees
  end_pos_lat_lng_deg?: [number, number]; // [latitude, longitude] in degrees
}

export interface PowerSample {
  timestamp?: string;
  timer_duration_seconds?: number | null;
  watts?: number;
}

export interface ActivityLog {
  dateID: string; // YYYYMMDD format from parent day post
  terraActivityDataID: string;
  startTime: string; // ISO date string
  endTime: string; // ISO date string
  duration?: number; // duration in minutes
  calories?: number;
  workKilojoules?: number;
  minHR?: number; // minimum heart rate in bpm
  aveHR?: number; // average heart rate in bpm
  maxHR?: number; // maximum heart rate in bpm
  avgWatts?: number; // average power in watts
  maxWatts?: number; // maximum power in watts
  powerSamples?: PowerSample[]; // cached per-sample power data from Terra
  sessionName?: string;
  linkedTrainingLogID?: string;
  provider?: string; // Terra provider (e.g., "GARMIN", "FITBIT")
  deviceModel?: string; // device model string, when provider's API exposes it
  ignore?: boolean; // whether to ignore the activity
  heartRateData?: ActivityHeartRateData; // cached HR data from Terra
  positionData?: PositionData; // GPS position data from Terra
  public?: boolean; // Whether this log is publicly readable
  linkedPostID?: string; // ID of the post this log is linked to
}

export interface WellnessLog {
  dateID: string; // YYYYMMDD format from parent day post
  health: number;
  mood: number;
  physical: number;
  recovery: number;
  sick: boolean;
  sleep: number; // this is where sleep is stored
  sleepTime: number;
  stress: number;
  timestamp: number; // GMT timestamp
  wellnessScore: number;
  soreness?: number; // 0-10 scale for soreness level
  injured?: boolean; // whether currently injured
  canTrainWithInjury?: boolean; // DEPRECATED: kept for backward compat with old logs
  trainingCapacity?: "full" | "modified" | "none"; // what training they can do while injured
}

export interface PeakScoreLog {
  dateID: string; // YYYYMMDD format from parent day post
  peakScore: number;
  wellnessFinal: number;
  sickFactor: number;
  hlr: number;
  workloadFactor: number;
  workloadFactorDisplay?: number; // Wider-bounded value for the Training Load UI; workloadFactor stays tightly capped for peakScore math
  loadBalanceProvisional?: boolean; // True while fewer than 7 days of history exist. The factor is held at 1.
  EWMA_WorkA: number;
  EWMA_WorkC: number;
  wellnessA: number;
  sorenessMultiplier?: number; // Quadratic soreness multiplier (0.65-1.0 scale)
  injuryPenalty?: number; // Injury penalty applied to peak score for this day
  timestamp: number; // GMT timestamp
  notes?: string; // Optional notes about the calculation
}

export interface Comment {
  comment: string;
  timestamp: number; // GMT timestamp
  userID: string;
}

export interface CommentWithUser extends Comment {
  id: string;
  user?: {
    name?: string;
    surname?: string;
    imageUrl?: string;
    imageUrlTiny?: string;
  };
}

export interface ActivityMeasurement {
  ActivityTypeID: string;
  activityName?: string; // Denormalized activity name captured at log time so other accounts (e.g. a coach) can display it without access to the owner's activity library
  variables: ActivityVariable[]; // Array of variable measurements for this activity
  timeLogged?: string; // Optional - undefined for planned activities, set when logged
  activityIndex?: number; // Optional index (may be used for ordering)
  globalCategoryID?: string; // If set, ActivityTypeID refers to a global activity within this category (from admin activity packs)
  supersetGroupId?: string; // If set, links this measurement with others sharing the same ID into a superset (alternating sets)
  note?: string; // Optional coach note for this activity, visible to the athlete (read-only for athletes)
  blockId?: string; // Id of the ActivityBlock/section this measurement belongs to (for ordered section rendering incl. Session Notes)
  blockName?: string; // Denormalized block/section name captured at save time
}

// An ordered section within a session. Either an activity section or a
// coach-authored "Session Note" (rich text) section. Authored in the Elite
// coach portal; rendered read-only here.
export interface ActivityBlock {
  id: string;
  name: string;
  ActivityMeasurementList: ActivityMeasurement[];
  loadedTemplateId?: string;
  isIndividual?: boolean;
  blockType?: "activities" | "notes";
  notesContent?: string; // Markdown content for a "notes" block
}

// Periodisation phase options for training sessions
export type PeriodisationType =
  | "accumulation"
  | "building"
  | "deload"
  | "familiarisation"
  | "intensification"
  | "maintenance"
  | "prehab"
  | "rehab"
  | "taper";

export const PERIODISATION_OPTIONS: {
  value: PeriodisationType;
  label: string;
}[] = [
  { value: "accumulation", label: "Accumulation" },
  { value: "building", label: "Building" },
  { value: "deload", label: "Deload" },
  { value: "familiarisation", label: "Familiarisation" },
  { value: "intensification", label: "Intensification" },
  { value: "maintenance", label: "Maintenance" },
  { value: "prehab", label: "Prehab" },
  { value: "rehab", label: "Rehab" },
  { value: "taper", label: "Taper" },
];

export interface ProgramWeek {
  weekNumber: number;
  periodisation?: PeriodisationType;
}

export interface ProgramSessionReference {
  sessionId: string;
  plannedDate: string;
  weekNumber?: number;
  dayNumber?: number;
}

export interface ProgramLinkage {
  programId: string;
  programName: string;
  durationWeeks: number;
  weekNumber?: number;
  dayNumber?: number;
}

export interface Program {
  id?: string;
  name: string;
  description: string;
  startDate: string;
  durationWeeks: number;
  weeks: ProgramWeek[];
  linkedSessions: ProgramSessionReference[];
  isActive?: boolean; // Whether this program is active and sessions appear on the athlete's calendar
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Session {
  id?: string;
  name?: string;
  plannedDate: string;
  startTime?: string;
  endTime?: string;
  ActivityMeasurementList?: ActivityMeasurement[]; // All activities (planned and logged). Planned ones have timeLogged undefined
  activityBlocks?: ActivityBlock[]; // Ordered sections (activity + Session Note blocks) preserving the coach's layout
  linkedTrainingLogID?: string;
  linkedPerformanceLogID?: string; // ID of the performance log this session is linked to
  trainingTypeID?: string; // ID reference into the trainingTypes collection
  /** @deprecated Legacy composite ID (`${sportId}:${sessionTypeName}`) kept for old docs; use trainingTypeID */
  sessionTypeID?: string;
  periodisation?: PeriodisationType; // Training periodisation phase
  /** Optional link to an event; target values use ActivityVariable.goal. */
  eventId?: string;
  description: string;
  status: "proposed" | "planned" | "executing" | "complete" | "rejected";
  /** True when the session was created by pressing Start (ad-hoc), not scheduled beforehand. Cancelling deletes it instead of resetting it. */
  startedAdHoc?: boolean;
  rejectionReason?: string; // Reason the athlete declined a proposed session
  rejectedAt?: Date; // When the session was declined
  public?: boolean; // Whether this session is publicly readable
  linkedPostID?: string; // ID of the post this session is linked to
  createdAt?: Date;
  updatedAt?: Date;
  programLinkage?: ProgramLinkage;
  coachIssued?: boolean; // Whether this session was issued by a coach
  coachID?: string; // ID of the coach who issued this session
  organisationID?: string; // ID of the organisation this session belongs to
  coach?: {
    name?: string;
    surname?: string;
    imageUrl?: string;
    imageUrlTiny?: string;
  };
}

export interface SessionTemplate {
  id?: string;
  name?: string; // Optional session name
  ActivityMeasurementList?: ActivityMeasurement[]; // All activities (planned and logged). Planned ones have timeLogged undefined
  trainingTypeID?: string; // ID reference into the trainingTypes collection
  /** @deprecated Legacy composite ID (`${sportId}:${sessionTypeName}`) kept for old docs; use trainingTypeID */
  sessionTypeID?: string;
  periodisation?: PeriodisationType; // Training periodisation phase
  description?: string;
}

export interface DayPost {
  dateID: string;
  negativeInfluencesLogs: (NegativeInfluencesLog & { id: string })[];
  trainingLogs: (TrainingLog & { id: string })[];
  recoveryLogs: (RecoveryLog & { id: string })[];
  niggleLogs: (NiggleLog & { id: string })[];
  performanceLogs: (PerformanceLog & { id: string })[];
  healthLogs: (HealthLog & { id: string })[];
  stepLogs: (StepLog & { id: string })[];
  noteLogs: (NoteLog & { id: string })[];
  wellnessLogs: (WellnessLog & { id: string })[];
  activityLogs: (ActivityLog & { id: string })[]; // Activity logs from Terra
  femaleCycleLogs: (FemaleCycleLog & { id: string })[]; // Female cycle tracking logs
  wearableDayData?: string[]; // List of Terra daily document paths in (default) DB
  peakScoreLog?: PeakScoreLog & { id: string }; // Only one peak score log per day
  sessions: (Session & { id: string })[]; // Sessions for this day
  likes: string[]; // userIDs added via a filebase function
  comments: Comment[]; // comments added via a filebase function
}

export interface DayPostReferences {
  negativeInfluenceLogIDs: string[];
  trainingLogIDs: string[]; // trainingLogIDs added by the user
  recoveryLogIDs: string[]; // recoveryLogIDs added by the user
  niggleLogIDs: string[]; // niggleLogIDs added by the user
  performanceLogIDs: string[]; // performanceLogIDs added by the user
  healthLogIDs: string[]; // healthLogIDs added by the user
  stepLogIDs: string[]; // stepLogIDs added by the user
  noteLogIDs: string[]; // noteLogIDs added by the user
  wellnessLogIDs: string[]; // wellnessLogIDs added by the user
  activityLogIDs: string[]; // activityLogIDs added via Terra integration
  femaleCycleLogIDs: string[]; // femaleCycleLogIDs added by the user
  wearableDayData?: string[]; // Terra daily doc paths stored for this day
  peakScoreLogID?: string; // Only one peak score log ID per day
  sessionIDs: string[]; // sessionIDs added by the user
  likes: string[]; // userIDs, added via a filebase function
  commentIDs: string[]; // commentIDs, added via a filebase function
}
